import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminVerificationsApi } from '../api/adminApi';
import { VerificationAction, LoadingStatus } from '../../types';

interface AdminVerificationsState {
  items: VerificationAction[];
  total: number;
  status: LoadingStatus;
  error: string | null;
}

const initialState: AdminVerificationsState = { items: [], total: 0, status: 'idle', error: null };

export const fetchVerifications = createAsyncThunk(
  'adminVerifications/fetch',
  async (params: Record<string, unknown> = {}, { rejectWithValue }) => {
    try {
      const res = await adminVerificationsApi.list(params);
      return res.data;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to fetch');
    }
  },
);

export const approveVerification = createAsyncThunk(
  'adminVerifications/approve',
  async (id: string, { rejectWithValue }) => {
    try { await adminVerificationsApi.approve(id); return id; }
    catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to approve');
    }
  },
);

export const rejectVerification = createAsyncThunk(
  'adminVerifications/reject',
  async ({ id, reason }: { id: string; reason: string }, { rejectWithValue }) => {
    try { await adminVerificationsApi.reject(id, reason); return id; }
    catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to reject');
    }
  },
);

const slice = createSlice({
  name: 'adminVerifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchVerifications.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(fetchVerifications.fulfilled, (state, { payload }) => {
        state.items = payload.items;
        state.total = payload.total;
        state.status = 'succeeded';
      })
      .addCase(fetchVerifications.rejected, (state, { payload }) => {
        state.items = [];
        state.total = 0;
        state.status = 'failed';
        state.error = (payload as string) || 'Failed to fetch';
      })
      .addCase(approveVerification.fulfilled, (state, { payload }) => {
        state.items = state.items.filter((v) => v._id !== payload);
        state.total -= 1;
      })
      .addCase(rejectVerification.fulfilled, (state, { payload }) => {
        state.items = state.items.filter((v) => v._id !== payload);
        state.total -= 1;
      });
  },
});

export default slice.reducer;
export const selectVerifications = (s: { adminVerifications: AdminVerificationsState }) => s.adminVerifications.items;
export const selectVerificationsTotal = (s: { adminVerifications: AdminVerificationsState }) => s.adminVerifications.total;
export const selectVerificationsStatus = (s: { adminVerifications: AdminVerificationsState }) => s.adminVerifications.status;
export const selectVerificationsError = (s: { adminVerifications: AdminVerificationsState }) => s.adminVerifications.error;
